import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { Modal, Tag, Typography } from 'antd';
// API
import { GET_CB_PROMO_CODE_REDEMPTIONS } from 'api/cbPromocodes/queries';
// Types
import { SortDirection } from 'api/graphql-global-types';
import {
  GetCbPromoCodeRedemptions,
  GetCbPromoCodeRedemptionsVariables,
  GetCbPromoCodeRedemptions_getCbPromoCodeRedemptions_entities,
} from 'api/cbPromocodes/types/GetCbPromoCodeRedemptions';
// UI
import Table from 'ui/Table';
import CopyLink from 'ui/CopyLink/CopyLink';
import StatisticColumns, {
  StatisticColumnItem,
} from 'ui/StatisticColumns/StatisticColumns';
// Helpers
import { formatCurrencyString } from 'helpers/utils';
import {
  CbPromoCode,
  buildPromoCodeShareLink,
  getPromoCodeKindLabel,
  getPromoCodeValueLabel,
} from 'helpers/cbPromoCodes/helpers';
import { CB_PROMO_CODE_STATUS_COLORS } from 'helpers/cbPromoCodes/constants';

const { Text } = Typography;

type Redemption = GetCbPromoCodeRedemptions_getCbPromoCodeRedemptions_entities;

export type RedemptionsModalProps = {
  promoCode: CbPromoCode;
  show: boolean;
  onCancel: () => void;
};

const getAnalyticsItems = (promoCode: CbPromoCode): StatisticColumnItem[] => {
  switch (promoCode.__typename) {
    case 'CbCreditFreePromoCode':
    case 'CbCreditMatchPromoCode':
      return [
        {
          title: 'Promo dollars issued',
          value: formatCurrencyString(promoCode.promoIssuedTotal),
          span: 8,
        },
      ];
    case 'CbSubscriptionDiscountPromoCode':
      return [
        {
          title: 'Revenue forgone',
          value: formatCurrencyString(promoCode.discountGrantedTotal),
          span: 8,
        },
      ];
    case 'CbSubscriptionTrialPromoCode':
      return [
        {
          title: 'Trials started',
          value: promoCode.trialMetrics.trialsStarted,
          span: 6,
        },
        {
          title: 'In progress',
          value: promoCode.trialMetrics.trialsInProgress,
          span: 6,
        },
        {
          title: 'Converted',
          value: promoCode.trialMetrics.trialsConverted,
          span: 6,
        },
        {
          title: 'Conversion',
          value:
            promoCode.trialMetrics.conversionRate != null
              ? `${Math.round(promoCode.trialMetrics.conversionRate * 100)}%`
              : '—',
          span: 6,
        },
      ];
    default:
      return [];
  }
};

const RedemptionsModal: React.FC<RedemptionsModalProps> = ({
  promoCode,
  show,
  onCancel,
}) => {
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { data, loading } = useQuery<
    GetCbPromoCodeRedemptions,
    GetCbPromoCodeRedemptionsVariables
  >(GET_CB_PROMO_CODE_REDEMPTIONS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        promoCodeId: promoCode.id,
        direction: SortDirection.DESC,
        limit: pageSize,
        offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
      },
    },
  });

  const redemptions = data?.getCbPromoCodeRedemptions;

  const columns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) =>
        value ? moment(value).format('MMM DD YYYY, HH:mm') : '-',
    },
    {
      title: 'Account',
      dataIndex: 'ownerId',
      key: 'ownerId',
      render: (_: string, record: Redemption) =>
        record.teamId ? `${record.ownerId} (team)` : record.ownerId,
    },
    {
      title: 'Performed by',
      dataIndex: 'performedByName',
      key: 'performedByName',
      render: (_: string, record: Redemption) =>
        record.performedByName || record.performedByEmail || '-',
    },
    {
      title: 'Credit granted',
      dataIndex: 'creditAmountGranted',
      key: 'creditAmountGranted',
      render: (value: number | null) =>
        value != null ? formatCurrencyString(value) : '-',
    },
    {
      title: 'Subscription',
      dataIndex: 'subscriptionId',
      key: 'subscriptionId',
      render: (value: string | null) => value || '-',
    },
    {
      title: 'Ledger tx',
      dataIndex: 'balanceTransactionId',
      key: 'balanceTransactionId',
      render: (value: string | null) => value || '-',
    },
  ];

  return (
    <Modal
      title={
        <span>
          {`"${promoCode.code}" `}
          <Tag color={CB_PROMO_CODE_STATUS_COLORS[promoCode.status]}>
            {promoCode.status}
          </Tag>
        </span>
      }
      visible={show}
      width={960}
      footer={null}
      onCancel={onCancel}
    >
      <StatisticColumns
        gutter={16}
        style={{ marginBottom: 8 }}
        items={[
          {
            title: getPromoCodeKindLabel(promoCode.kind),
            value: getPromoCodeValueLabel(promoCode),
            span: 8,
            valueStyle: { fontSize: 18 },
          },
          {
            title: 'Uses',
            value:
              promoCode.remainingUses == null
                ? `${promoCode.usedCount} / ∞`
                : `${promoCode.usedCount} / ${
                    promoCode.usedCount + promoCode.remainingUses
                  }`,
            span: 8,
          },
          {
            title: 'Per-account limit',
            value: promoCode.perAccountLimit,
            span: 8,
          },
        ]}
      />

      <StatisticColumns
        style={{ marginBottom: 16 }}
        items={getAnalyticsItems(promoCode)}
      />

      <div style={{ marginBottom: 16 }}>
        <CopyLink
          name="cb-promo-code-share-link"
          label="Shareable link"
          link={buildPromoCodeShareLink(promoCode.code)}
        />
      </div>

      {promoCode.note && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Note: {promoCode.note}
        </Text>
      )}

      <Table<Redemption, string>
        columns={columns}
        data={redemptions?.entities}
        loading={loading}
        total={redemptions?.total}
        currentPage={currentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setCurrentPage={setCurrentPage}
        scroll={{ x: 800 }}
      />
    </Modal>
  );
};

export default RedemptionsModal;
